import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, ExternalLink, ArrowRightLeft, CalendarClock, Mail, Phone, AlertTriangle } from "lucide-react";
import { StatusBadge, fmtDate } from "../../lib/status";
import ReassignVehicleModal from "../ReassignVehicleModal";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Lightweight action panel for a single booking block: deliberately
 * limited to the handful of actions staff actually need at a glance
 * (per Phase 2F's "don't overload the block with every action"). */
export function BookingBlockPopover({ bookingId, open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [editingDates, setEditingDates] = useState(false);
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [savingDates, setSavingDates] = useState(false);

  useEffect(() => {
    if (!open || !bookingId) { setData(null); setEditingDates(false); return; }
    setLoading(true);
    api.get(`/bookings/${bookingId}`)
      .then(({ data }) => {
        setData(data);
        setNewStart(toLocalInput(data.booking.start_datetime));
        setNewEnd(toLocalInput(data.booking.end_datetime));
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, bookingId]);

  if (!open) return null;

  const b = data?.booking;
  const risk = data?.risk_reasons || [];

  const saveDates = async () => {
    if (!newStart || !newEnd) return;
    setSavingDates(true);
    try {
      await api.put(`/bookings/${bookingId}`, {
        start_datetime: new Date(newStart).toISOString(),
        end_datetime: new Date(newEnd).toISOString(),
      });
      toast.success("Booking dates updated");
      setEditingDates(false);
      onChanged?.();
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 409
        ? "Those dates have a conflict: open the booking to review and override."
        : formatApiError(e));
    } finally {
      setSavingDates(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} data-testid="fc-popover-backdrop" />
      <div
        className="fixed inset-x-2 bottom-2 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-50 w-auto sm:w-full sm:max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-5 max-h-[85vh] overflow-y-auto"
        data-testid="fc-booking-popover"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {loading || !b ? (
              <div className="text-sm text-slate-500" data-testid="fc-popover-loading">Loading…</div>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg font-semibold truncate">{b.customer_name}</h3>
                  <StatusBadge status={b.status} isHold={b.is_hold} changeRequested={b.change_requested}
                               cancelledBy={b.cancelled_by} cancellationReason={b.cancellation_reason}
                               reconfirmationRequired={b.reconfirmation_required} />
                </div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">{b.booking_reference}</div>
                <div className="text-sm text-slate-600 mt-1">
                  {b.vehicle?.name} · {fmtDate(b.start_datetime)} → {fmtDate(b.end_datetime)}
                </div>
              </>
            )}
          </div>
          <button onClick={onClose} data-testid="fc-popover-close" className="grid min-h-11 min-w-11 place-items-center rounded-md hover:bg-slate-100 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {risk.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-start gap-1.5"
               data-testid="fc-popover-issue">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{risk[0].message}</span>
          </div>
        )}

        {!loading && b && !editingDates && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Link to={`/app/bookings/${b.id}`} data-testid="fc-popover-open"
                  className="col-span-2 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium text-center hover:bg-[#2D6A4F]">
              Open booking
            </Link>
            <button onClick={() => setReassignOpen(true)} data-testid="fc-popover-reassign"
                    className="px-3 py-2 rounded-lg border border-slate-300 text-sm inline-flex items-center justify-center gap-1.5 hover:bg-slate-50">
              <ArrowRightLeft className="w-3.5 h-3.5" /> Reassign
            </button>
            <button onClick={() => setEditingDates(true)} data-testid="fc-popover-change-dates"
                    className="px-3 py-2 rounded-lg border border-slate-300 text-sm inline-flex items-center justify-center gap-1.5 hover:bg-slate-50">
              <CalendarClock className="w-3.5 h-3.5" /> Change dates
            </button>
            {b.customer_email && (
              <a href={`mailto:${b.customer_email}`} data-testid="fc-popover-email"
                 className="px-3 py-2 rounded-lg border border-slate-300 text-sm inline-flex items-center justify-center gap-1.5 hover:bg-slate-50">
                <Mail className="w-3.5 h-3.5" /> Email
              </a>
            )}
            {b.customer_phone && (
              <a href={`tel:${b.customer_phone}`} data-testid="fc-popover-call"
                 className="px-3 py-2 rounded-lg border border-slate-300 text-sm inline-flex items-center justify-center gap-1.5 hover:bg-slate-50">
                <Phone className="w-3.5 h-3.5" /> Call
              </a>
            )}
            {data?.confirmation_link && (
              <a href={data.confirmation_link} target="_blank" rel="noreferrer" data-testid="fc-popover-confirmation"
                 className="col-span-2 px-3 py-2 rounded-lg border border-slate-300 text-sm inline-flex items-center justify-center gap-1.5 hover:bg-slate-50">
                <ExternalLink className="w-3.5 h-3.5" /> View confirmation link
              </a>
            )}
          </div>
        )}

        {!loading && b && editingDates && (
          <div className="mt-4 space-y-3" data-testid="fc-popover-dates-form">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Start</span>
              <input type="datetime-local" value={newStart} onChange={(e) => setNewStart(e.target.value)}
                     data-testid="fc-popover-dates-start"
                     className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:border-[#2D6A4F]" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">End</span>
              <input type="datetime-local" value={newEnd} onChange={(e) => setNewEnd(e.target.value)}
                     data-testid="fc-popover-dates-end"
                     className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:border-[#2D6A4F]" />
            </label>
            <p className="text-xs text-slate-500">Normal availability/conflict checks still run when you save.</p>
            <div className="flex gap-2">
              <button onClick={saveDates} disabled={savingDates} data-testid="fc-popover-dates-save"
                      className="flex-1 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#2D6A4F] disabled:opacity-60">
                {savingDates ? "Saving…" : "Save dates"}
              </button>
              <button onClick={() => setEditingDates(false)} data-testid="fc-popover-dates-cancel"
                      className="px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      <ReassignVehicleModal
        bookingId={bookingId} open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        onDone={() => { setReassignOpen(false); onChanged?.(); onClose(); }}
      />
    </>
  );
}
